import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import PillHeaderComponent from '../Shared/PillHeaderComponent';
import { PackageTrackingDetails, JobComment } from '../../../types';
import HistoryTableComponent from './HistoryTableComponent';
import ShipmentDetailsComponent from './ShipmentDetailsComponent';
import RequestChangeComponent from '../RequestChange/RequestChangeComponent';
import MapComponent from './MapComponent';
import ProgressComponent from './ProgressComponent';
import PageTitle from '../Shared/PageTitle';
import withSuspense from '../Shared/withSuspense';
import UnconfirmedEmailComponent from '../Shared/UnconfirmedEmailComponent';
import DiscussionComponent from './DiscussionComponent';
import NoUpdateComponent from '../RequestChange/NoUpdateComponent';

const HeaderWrapper = styled.div`
    margin-bottom: 10px;
`;

const BottomSection = styled.div`
    padding: 16px 0;
    margin-top: 12px;
    border-top: 1px solid #C8CFD2;
`;

const MobileDiscussionPage = styled.div`
    position: absolute;
    top: 62px;
    bottom: 10px;
    left: 10px;
    right: 10px;
    display: flex;
    flex-direction: column;
`;

interface PackageDetailsProps {
    packageDetails: PackageTrackingDetails
    mapTileUrl: string;
    mapAttribution: string;
    customerSupportUrl: string;
    hidePhotos: boolean;
    loginUrl: string;
}

const MobileDiscussionComponent = ({ packageDetails, jobComments, setJobComments, canReply, setMobileDiscussionVisible }:
    {
        packageDetails: PackageTrackingDetails,
        jobComments: JobComment[],
        setJobComments: (value: JobComment[]) => void;
        canReply: boolean,
        setMobileDiscussionVisible: (state: boolean) => void
    }) => {
    const { t } = useTranslation();
    return (<MobileDiscussionPage id={'package_details'}>
        <div>
            <a href="#" onClick={() => setMobileDiscussionVisible(false)}>&larr; {t('package_tracking_discussion_back')}</a>
        </div>
        <DiscussionComponent packageDetails={packageDetails} jobComments={jobComments} setJobComments={setJobComments} canReply={canReply} />
    </MobileDiscussionPage>);
}

const PackageDetailsComponent = ({ packageDetails, mapTileUrl, mapAttribution, customerSupportUrl, hidePhotos, loginUrl }: PackageDetailsProps) => {
    const [isRequestChangeCollapsed, setIsRequestChangeCollapsed] = useState(packageDetails.LayoutOptions.IsRequestChangeCollapsed);
    const [isHistoryCollapsed, setIsHistoryCollapsed] = useState(packageDetails.LayoutOptions.IsHistoryCollapsed);
    const [mobileDiscussionVisible, setMobileDiscussionVisible] = useState(false);

    const [jobComments, setJobComments] = useState(packageDetails.JobComments);

    const [t] = useTranslation();

    // https://stackoverflow.com/questions/54491645/media-query-syntax-for-reactjs
    const mediaMaxWidth = "(max-width: 480px)";
    const [mediaMatches, setMediaMatches] = useState(window.matchMedia(mediaMaxWidth).matches);

    useEffect(() => {
        window.matchMedia(mediaMaxWidth).addEventListener('change', e => setMediaMatches(e.matches));
    }, []);

    const canViewDiscussion = packageDetails.UserOwnsPackage && packageDetails.JobCommentsEnabled;
    const canReply = packageDetails.UserOwnsPackage && !packageDetails.IsComplete;

    const canUpdatePackage = packageDetails.UserOwnsPackage &&
        !packageDetails.IsComplete &&
        packageDetails.UpdateAllowed &&
        (
            packageDetails.DeliveryOptions.CanUpdateSafePlace ||
            packageDetails.DeliveryOptions.CanUpdateNeighboursAddress ||
            packageDetails.DeliveryOptions.CanUpdateServiceLocation ||
            packageDetails.DeliveryOptions.CanUpdateIntendedDate ||
            packageDetails.DeliveryOptions.CanCancel
        );

    const canRequestChange = canUpdatePackage && packageDetails.IsUserAuthenticated;

    const showDiscussionOnLeft = canViewDiscussion;
    const showRequestChangeOnLeft = canRequestChange && !showDiscussionOnLeft;

    const showMap = packageDetails.UserOwnsPackage &&
        packageDetails.ExpectDetail?.ShowLocation &&
        packageDetails.ExpectDetail.AddressLatitude &&
        packageDetails.ExpectDetail.AddressLongitude;

    if (mediaMatches && mobileDiscussionVisible && canViewDiscussion) {
        return (<MobileDiscussionComponent
            packageDetails={packageDetails}
            jobComments={jobComments}
            setJobComments={setJobComments}
            canReply={canReply}
            setMobileDiscussionVisible={setMobileDiscussionVisible} />);
    }

    return (
        <div id={'package_details'}>
            <PageTitle>{packageDetails.TrackingNumber + (packageDetails.FriendlyName ? ` - ${packageDetails.FriendlyName}` : ``)}</PageTitle>

            {packageDetails.IsUserAuthenticated && !packageDetails.EmailConfirmed && 
                <UnconfirmedEmailComponent />}

            {/* Left: Progress Bar + Discussion or Request Change */}
            <div className={'pieces'}>
                <div className={'piece left'}>
                    {/* Progress */}
                    <HeaderWrapper id='progress_header'>
                        <PillHeaderComponent
                            title={t('package_tracking_details_progress')}
                            isCollapsible={false}
                        />
                    </HeaderWrapper>
                    <ProgressComponent packageDetails={packageDetails} />
                    {showDiscussionOnLeft &&
                        <>
                            <HeaderWrapper id='messages_header'>
                                <PillHeaderComponent
                                    title={t('package_tracking_details_messages')}
                                    isCollapsible={false}
                                />
                        </HeaderWrapper>
                        <DiscussionComponent
                            packageDetails={packageDetails}
                            jobComments={jobComments}
                            setJobComments={setJobComments}
                            canReply={canReply}
                            preview={mediaMatches}
                            setMobileDiscussionVisible={setMobileDiscussionVisible} />
                        </>
                    }
                    {showRequestChangeOnLeft &&
                        <>
                            <HeaderWrapper id='requestchange_header'>
                                <PillHeaderComponent
                                    title={t('package_tracking_options_request_change')}
                                    isCollapsible={false}
                                />
                            </HeaderWrapper>
                            <RequestChangeComponent packageDetails={packageDetails} />
                        </>
                    }
                </div>
                <div id='map_section' className={'piece'}>
                    {/* Map */}
                    {showMap &&
                        <div id={'expectDetail'}>
                            <HeaderWrapper id='map_header'>
                                <PillHeaderComponent
                                    title={t('package_tracking_details_location')}
                                    isCollapsible={false}
                                />
                            </HeaderWrapper>
                            <MapComponent
                                trackingNumber={packageDetails.TrackingNumber}
                                mapTileUrl={mapTileUrl}
                                mapAttribution={mapAttribution}
                            />
                        </div>
                    }
                </div>
            </div>

            <BottomSection>
                { /* Shipment details */ }
                <HeaderWrapper id='details_header'>
                    <PillHeaderComponent
                        title={t('package_tracking_details_shipment_details')}
                        isCollapsible={false}
                    />
                </HeaderWrapper>
                <ShipmentDetailsComponent packageDetails={packageDetails} />

                {(!showRequestChangeOnLeft && canRequestChange) &&
                    <>
                        <HeaderWrapper id='requestchange_header'>
                            <PillHeaderComponent
                                title={t('package_tracking_options_request_change')}
                                isCollapsible={true}
                                isCollapsed={isRequestChangeCollapsed}
                                toggleCollapse={() => setIsRequestChangeCollapsed(!isRequestChangeCollapsed)}
                            />
                        </HeaderWrapper>
                        {!isRequestChangeCollapsed &&
                            <RequestChangeComponent packageDetails={packageDetails} />}
                    </>
                }

                {/* Action History */ }
                <HeaderWrapper id='history_header'>
                    <PillHeaderComponent
                        title={t('package_tracking_details_history')}
                        isCollapsible={true}
                        isCollapsed={isHistoryCollapsed}
                        toggleCollapse={() => setIsHistoryCollapsed(!isHistoryCollapsed)}
                    />
                </HeaderWrapper>
                {!isHistoryCollapsed &&
                    <HistoryTableComponent packageDetails={packageDetails} hideAllPhotos={hidePhotos} />}

                {!canRequestChange && <NoUpdateComponent
                    isAuthenticated={packageDetails.IsUserAuthenticated} loginUrl={loginUrl} customerSupportUrl={customerSupportUrl} />}
            </BottomSection>
        </div>
    );
};

export default withSuspense(PackageDetailsComponent);